import helpers from '../commonHelpers/helpers'
import { CONFIG } from '../config/config'
import FirebaseApp from './firebaseApp'

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryDocumentSnapshot,
  QueryFieldFilterConstraint,
  QuerySnapshot,
  setDoc,
  startAfter,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore'

const createItem = async (collection_name: string, document: any) => {
  return addDoc(collection(FirebaseApp.firestore, collection_name), document)
}

const getItem = async (collection_name: string, id: string) => {
  return getDoc(doc(collection(FirebaseApp.firestore, collection_name), id))
}

const getItemsUsingIds = async (
  collection_name: string,
  idsList: string[]
): Promise<QueryDocumentSnapshot<DocumentData>[]> => {
  let idsLocalList: string[] = []
  let fetchedDocsCount: number = 0
  let idsOfCurrentChunk: string[] = []
  let currDocsHolder: QuerySnapshot<DocumentData> = [] as any
  let allDocsHolder: QueryDocumentSnapshot<DocumentData>[] = []

  const CHUNK_SIZE = 10

  try {
    while (allDocsHolder.length !== idsList.length || allDocsHolder.length > idsList.length) {
      idsLocalList = [...idsList]
      idsOfCurrentChunk = idsLocalList.splice(fetchedDocsCount, CHUNK_SIZE)

      if (!idsOfCurrentChunk.length) break

      currDocsHolder = await filterItems(collection_name, [
        where(documentId(), 'in', idsOfCurrentChunk),
      ])

      currDocsHolder.forEach((doc) => {
        allDocsHolder.push(doc)
      })

      fetchedDocsCount += idsOfCurrentChunk.length
    }
  } catch (err) {
    if (CONFIG.RUN_MODE !== 'production')
      helpers.logger({
        isError: true,
        message: err,
      })
  } finally {
    return allDocsHolder
  }
}

const listItems = async (
  collection_name: string,
  orderByField?: string,
  orderByDirection?: any,
  perPage?: number,
  cursorId?: any
) => {
  const collectionRef = collection(FirebaseApp.firestore, collection_name)

  const queryConstraints = []

  if (orderByField && !orderByDirection) {
    queryConstraints.push(orderBy(orderByField, 'asc'))
  }

  if (orderByField && orderByDirection) {
    queryConstraints.push(orderBy(orderByField, orderByDirection))
  }

  if (perPage) {
    queryConstraints.push(limit(2))
  }

  if (cursorId) {
    const cursorDocument = await getItem(collection_name, cursorId)
    queryConstraints.push(startAfter(cursorDocument))
  }

  const firestoreQuery = query(collectionRef, ...queryConstraints)

  return getDocs(firestoreQuery)
}

const filterItems = async (
  collection_name: string,
  queries?: any[],
  perPage?: number | null,
  orderByField?: string | null,
  orderByDirection?: any | null,
  cursorId?: any
) => {
  const collectionRef = collection(FirebaseApp.firestore, collection_name)

  let queryConstraints: any[] = []

  if (orderByField && !orderByDirection) {
    queryConstraints.push(orderBy(orderByField, 'asc'))
  }

  if (orderByField && orderByDirection) {
    queryConstraints.push(orderBy(orderByField, orderByDirection))
  }

  if (perPage) {
    queryConstraints.push(limit(perPage))
  }

  if (cursorId) {
    const cursorDocument = await getItem(collection_name, cursorId)
    queryConstraints.push(startAfter(cursorDocument))
  }

  const firestoreQuery = query(collectionRef, ...queryConstraints, ...(queries ?? []))

  return getDocs(firestoreQuery)
}

export const listenItems = ({
  collection_name,
  queries,
  constraints,
  callback,
}: {
  collection_name: string
  queries?: QueryConstraint[]
  constraints?: QueryCompositeFilterConstraint
  callback: (items: QuerySnapshot<DocumentData, DocumentData>) => void
}): Unsubscribe => {
  const ref = collection(FirebaseApp.firestore, collection_name)

  if (constraints) {
    const q = query(ref, constraints)
    return onSnapshot(q, (snap) => {
      callback(snap)
    })
  } else {
    const q = query(ref, ...(queries || []))
    return onSnapshot(q, (snap) => {
      callback(snap)
    })
  }
}

const updateItem = async (collection_name: string, collection_id: any, data: any) => {
  return updateDoc(doc(FirebaseApp.firestore, collection_name, collection_id), data)
}

const deleteItem = async (collection_name: string, collection_id: string) => {
  return deleteDoc(doc(FirebaseApp.firestore, collection_name, collection_id))
}

const createItemWithCustomId = async (collection_name: string, custom_id: string, data: any) => {
  await setDoc(doc(FirebaseApp.firestore, collection_name, custom_id), data)
}

const getDocumentsCount = async (args: {
  collectionName: string
  queries: QueryFieldFilterConstraint[]
}) => {
  const collectionRef = collection(FirebaseApp.firestore, args.collectionName)
  const query_ = query(collectionRef, ...args.queries)
  const snapshot = await getCountFromServer(query_)
  return snapshot.data().count
}

const FirestoreService = {
  getItem,
  listItems,
  deleteItem,
  createItem,
  updateItem,
  filterItems,
  getItemsUsingIds,
  getDocumentsCount,
  createItemWithCustomId,
  listenItems,
}

export default FirestoreService
